import { z } from 'zod';

import { canonicalJson } from './canonical-json.ts';
import { DomainError } from './errors.ts';
import { type StudyStepSlug, studyStep } from './steps.ts';

/**
 * The backstop that stops a study the platform will not run from being paid for or fielded.
 *
 * Refusal is meant to be a conversational behaviour: the agent declines early, explains why, and
 * offers what it *can* do, which is both kinder and cheaper than letting someone draft a whole study
 * and be turned away at the till. But a prompt is guidance, not a guarantee — a model that has been
 * talked around, or a study whose problem only becomes clear once the audience is written, would
 * otherwise reach fielding with nothing left to stop it. So the whole study is reviewed against the
 * policy once more before money is taken.
 *
 * The review is a stored verdict rather than a call: a model cannot run under the study row lock a
 * purchase holds, so a review is requested from Launch, answered off the request path and stored on
 * the study against exactly the versions it judged and the policy it judged them under
 * (`PolicyReviewRequest`). What runs under the lock is `policyReviewOutcome`, a pure comparison of
 * that verdict with the versions the lock reads. Purchase creation admits a study only on an allow
 * for its current versions under the active policy; the permanent lock and provisioning's first
 * step admit one only on an allow for its current versions under any policy, because a purchase
 * holds those versions and the review that admitted it, and a policy that changed after payment
 * does not reopen a study already paid for.
 *
 * Nothing is carried from one review to the next. Every input change moves a version, and a
 * verdict on versions the study no longer holds admits nothing.
 */

/**
 * The steps a refusal can name: each is where the researcher changes what was refused. A Stimulus
 * candidate is a `materials` problem; an image the guide shows is a `guide` problem, because the
 * guide is where it is placed and where it stops being shown. The Study Type is not here — a
 * catalogue choice cannot be out of policy.
 */
export const POLICY_STEPS = [
  'materials',
  'goal',
  'questions',
  'guide',
  'audience',
] as const satisfies readonly StudyStepSlug[];
export type PolicyStep = (typeof POLICY_STEPS)[number];

/** The five things a study cannot do: the policy floor. */
export const POLICY_REFUSAL_CATEGORIES = [
  'personal_data',
  'minors',
  'illegal',
  'harmful',
  'screening',
] as const;
export type PolicyRefusalCategory = (typeof POLICY_REFUSAL_CATEGORIES)[number];

/** The longest guidance a refusal keeps, as a coverage reason is bounded. */
export const MAX_POLICY_GUIDANCE_CHARS = 300;

/** The most refusals one verdict holds. */
export const MAX_POLICY_REFUSALS = 5;

/**
 * One thing the study cannot do: the step that holds it, which floor item it falls under, and what
 * the researcher could change, in at most two sentences. `guidance` is `null` when there is none to
 * show; the category's own sentence stands alone then. It never names a policy clause.
 */
export const policyRefusalSchema = z.strictObject({
  step: z.enum(POLICY_STEPS),
  category: z.enum(POLICY_REFUSAL_CATEGORIES),
  guidance: z.string().max(MAX_POLICY_GUIDANCE_CHARS).nullable(),
});
export type PolicyRefusal = z.infer<typeof policyRefusalSchema>;

const artifactPinSchema = z.strictObject({ artifact_id: z.uuid(), version: z.int().positive() });

/**
 * Exactly what a review judged. The images are bound by the Materials version, since a study file
 * never changes; the screening questions and the Limitations by the audience version, since they
 * live in its content; and the chain by the same artifact and version pairs a purchase snapshots.
 */
export const policyReviewInputsSchema = z.strictObject({
  study_type_id: z.string().min(1),
  template_version: z.int().positive(),
  materials_version: z.int().positive(),
  goal_version: z.int().positive(),
  goal_option_id: z.uuid(),
  rqs: artifactPinSchema,
  dg: artifactPinSchema,
  audience: artifactPinSchema,
});
export type PolicyReviewInputs = z.infer<typeof policyReviewInputsSchema>;

/**
 * Whether two reviews judged the same study: every bound version equal. Compared structurally, so
 * a field the inputs gain is compared with the rest without being listed here.
 */
export const samePolicyReviewInputs = (
  left: PolicyReviewInputs,
  right: PolicyReviewInputs,
): boolean => canonicalJson(left) === canonicalJson(right);

/** What a settled review found: no refusals means the study may go ahead. */
export const policyReviewVerdictSchema = z.strictObject({
  refusals: z.array(policyRefusalSchema).max(MAX_POLICY_REFUSALS),
});
export type PolicyReviewVerdict = z.infer<typeof policyReviewVerdictSchema>;

export const POLICY_REVIEW_STATUSES = ['pending', 'ready', 'unreadable', 'unavailable'] as const;
export type PolicyReviewStatus = (typeof POLICY_REVIEW_STATUSES)[number];

/**
 * The latest request to review the study.
 *
 * - `pending` — no answer yet.
 * - `ready` — the study was reviewed, and `verdict` is what the review found.
 * - `unreadable` — the reviewer answered, and its answer was not a verdict.
 * - `unavailable` — no answer could be had.
 *
 * `policy_version` names the policy the review was asked under (`ContentPolicy.version` on the
 * server). A verdict is held only on a `ready` request.
 */
export const policyReviewRequestSchema = z
  .strictObject({
    id: z.uuid(),
    status: z.enum(POLICY_REVIEW_STATUSES),
    requested_at: z.iso.datetime(),
    policy_version: z.string().min(1),
    inputs: policyReviewInputsSchema,
    verdict: policyReviewVerdictSchema.nullable(),
  })
  .refine((request) => (request.status === 'ready') === (request.verdict !== null), {
    message: 'a verdict is held exactly when the request is ready',
  });
export type PolicyReviewRequest = z.infer<typeof policyReviewRequestSchema>;

export const POLICY_REVIEW_SCHEMA_VERSION = 1;

/** What `studies.policy_review` holds: the latest review request and its verdict. */
export const storedPolicyReviewSchema = z.strictObject({
  schema_version: z.literal(POLICY_REVIEW_SCHEMA_VERSION),
  request: policyReviewRequestSchema,
});
export type StoredPolicyReview = z.infer<typeof storedPolicyReviewSchema>;

/**
 * The stored request, or nothing. A shape this build cannot read is no review rather than an error,
 * which every gate reads as a review still to be asked for.
 */
export const parseStoredPolicyReview = (stored: unknown): PolicyReviewRequest | null => {
  if (stored === null || stored === undefined) {
    return null;
  }

  const parsed = storedPolicyReviewSchema.safeParse(stored);

  return parsed.success ? parsed.data.request : null;
};

/**
 * How long a review may stay pending before the study is treated as holding none: its job was lost
 * with a worker, expired or never ran, so the next request from Launch asks again. Read by the
 * Launch screen too, which stops waiting at the same moment. A placeholder.
 */
export const POLICY_REVIEW_WAIT_MS = 90_000;

/**
 * What the stored review means for the study as it stands now.
 *
 * - `allowed` — a verdict with no refusals.
 * - `refused` — a verdict with refusals.
 * - `pending` — still being reviewed, within `POLICY_REVIEW_WAIT_MS`.
 * - `required` — nothing that answers for this study: no review, one on other versions or under
 *   another policy, or one pending past its wait.
 * - `unavailable` — the review could not be had, or its answer could not be read.
 *
 * Every outcome but `allowed` refuses.
 */
export type PolicyReviewOutcome =
  | { kind: 'allowed' }
  | { kind: 'refused'; refusals: PolicyRefusal[] }
  | { kind: 'pending' }
  | { kind: 'required' }
  | { kind: 'unavailable' };

/**
 * What a review is held against: one policy's version, or `'any'` for a study whose versions a
 * purchase holds, where the review that admitted the purchase stands whatever the policy is now.
 */
export type PolicyVersionAsked = string | 'any';

const answersFor = (
  stored: PolicyReviewRequest,
  inputs: PolicyReviewInputs,
  policyVersion: PolicyVersionAsked,
): boolean =>
  samePolicyReviewInputs(stored.inputs, inputs) &&
  (policyVersion === 'any' || stored.policy_version === policyVersion);

const pendingPastWait = (stored: PolicyReviewRequest, now: Date): boolean =>
  now.getTime() - Date.parse(stored.requested_at) >= POLICY_REVIEW_WAIT_MS;

/**
 * The stored review held against the study's current versions. `inputs` is `null` for a study that
 * is not complete, which no review answers for.
 */
export const policyReviewOutcome = ({
  stored,
  inputs,
  policyVersion,
  now,
}: {
  stored: PolicyReviewRequest | null;
  inputs: PolicyReviewInputs | null;
  policyVersion: PolicyVersionAsked;
  now: Date;
}): PolicyReviewOutcome => {
  if (stored === null || inputs === null || !answersFor(stored, inputs, policyVersion)) {
    return { kind: 'required' };
  }

  switch (stored.status) {
    case 'pending':
      return pendingPastWait(stored, now) ? { kind: 'required' } : { kind: 'pending' };
    case 'ready': {
      const refusals = stored.verdict?.refusals ?? [];

      return refusals.length === 0 ? { kind: 'allowed' } : { kind: 'refused', refusals };
    }
    case 'unreadable':
    case 'unavailable':
      return { kind: 'unavailable' };
  }
};

/**
 * Whether the study needs a new review rather than the one it holds: it holds none, the one it
 * holds judged other versions or another policy, or it is pending past its wait. `retry` is Launch's
 * "Check again", which asks again only over a review that could not be had or read. A verdict is
 * never asked again for the same study: a refusal is final for the versions it judged, and the
 * researcher's recourse is to change the step it names, which is a new review.
 */
export const policyReviewRequestNeeded = ({
  stored,
  inputs,
  policyVersion,
  now,
  retry,
}: {
  stored: PolicyReviewRequest | null;
  inputs: PolicyReviewInputs;
  policyVersion: string;
  now: Date;
  retry: boolean;
}): boolean => {
  if (stored === null || !answersFor(stored, inputs, policyVersion)) {
    return true;
  }

  switch (stored.status) {
    case 'pending':
      return pendingPastWait(stored, now);
    case 'ready':
      return false;
    case 'unreadable':
    case 'unavailable':
      return retry;
  }
};

/**
 * What the researcher is told for each category, followed by the reviewer's guidance when there is
 * some, then the step to open. Never a policy clause.
 */
export const POLICY_REFUSAL_COPY: Readonly<Record<PolicyRefusalCategory, string>> = {
  personal_data: 'This study asks people for details that could identify or contact them.',
  minors: 'This study would include people under 18.',
  illegal: 'This study asks about or supports something illegal.',
  harmful: 'This study would show or ask for content that could harm the people taking part.',
  screening: 'This study would turn people away for not sharing something sensitive.',
};

/** One refusal as the researcher reads it: the category's sentence, the guidance, the step. */
export const policyRefusalMessage = (refusal: PolicyRefusal): string =>
  [POLICY_REFUSAL_COPY[refusal.category], refusal.guidance, `Open ${studyStep(refusal.step).name}.`]
    .filter((part): part is string => part !== null && part !== '')
    .join(' ');

/** What the researcher is told for a review that does not answer the purchase yet. */
const OUTCOME_REFUSAL_MESSAGES = {
  pending: 'Sigora is still checking your study.',
  required: 'Sigora checks your study before you pay. Open Launch to start the check.',
  unavailable: "Sigora couldn't check your study just now. Try again shortly.",
} as const;

/**
 * Refuses unless the outcome is `allowed`: `study_refused` for a verdict with refusals, carrying
 * each with its step, and one code per other outcome. Every refusal leaves the caller's transaction
 * to roll back, so nothing is written for a study that was not admitted.
 */
export const assertPolicyReviewAllows = (outcome: PolicyReviewOutcome, studyId: string): void => {
  switch (outcome.kind) {
    case 'allowed':
      return;
    case 'refused': {
      const [first] = outcome.refusals;

      throw new DomainError(
        'study_refused',
        first === undefined ? OUTCOME_REFUSAL_MESSAGES.required : policyRefusalMessage(first),
        { study_id: studyId, refusals: outcome.refusals },
      );
    }
    case 'pending':
      throw new DomainError('policy_review_pending', OUTCOME_REFUSAL_MESSAGES.pending, {
        study_id: studyId,
      });
    case 'required':
      throw new DomainError('policy_review_required', OUTCOME_REFUSAL_MESSAGES.required, {
        study_id: studyId,
      });
    case 'unavailable':
      throw new DomainError('policy_review_unavailable', OUTCOME_REFUSAL_MESSAGES.unavailable, {
        study_id: studyId,
      });
  }
};

/**
 * The review as the Launch step reads it: the stored request's status (`null` while there is none),
 * what it means for the study now, the refusals when it refused, and when it was asked.
 */
export interface PolicyReviewView {
  status: PolicyReviewStatus | null;
  outcome: PolicyReviewOutcome['kind'];
  refusals: PolicyRefusal[];
  requestedAt: string | null;
}

export const policyReviewView = (
  stored: PolicyReviewRequest | null,
  outcome: PolicyReviewOutcome,
): PolicyReviewView => ({
  status: stored?.status ?? null,
  outcome: outcome.kind,
  refusals: outcome.kind === 'refused' ? outcome.refusals : [],
  requestedAt: stored?.requested_at ?? null,
});

/**
 * The queue a study's policy review is answered on. The API process stores the request and
 * enqueues it; the worker, which is the only process holding a model, answers it.
 */
export const QUEUE_POLICY_REVIEW = 'policy.review';

/**
 * The job the API process sends. It names the request as well as the study, so a job for a request
 * a later one has replaced is recognised as stale and spends nothing.
 */
export const policyReviewPayloadSchema = z.strictObject({
  studyId: z.uuid(),
  requestId: z.uuid(),
});
export type PolicyReviewPayload = z.infer<typeof policyReviewPayloadSchema>;
